if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get productMediaWrapper() {
        return (this._productMediaWrapper = this._productMediaWrapper || this.querySelector('.product__media-wrapper'));
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }

        if (this.productMediaWrapper) {
          this.blockPositionHandler = this.handleBlocksPosition.bind(this);
          this.handleBlocksPosition();
          document.addEventListener('matchMobile', this.blockPositionHandler);
          document.addEventListener('unmatchMobile', this.blockPositionHandler);
        }
      }

      handleBlocksPosition() {
        const isMobile = FoxTheme.config.mqlMobile;
        const blocks = this.querySelectorAll(`.product__block[data-show-below-media="true"]`);

        blocks.forEach((block) => {
          const blockId = block.dataset.blockId;
          const blockPlacement = this.productMediaWrapper.querySelector(`[data-block-id="${blockId}"]`);
          if (!blockPlacement) return;

          if (isMobile) {
            const isMoveToMedia = block.hasAttribute('data-moved-below-media');
            if (isMoveToMedia) {
              block.innerHTML = blockPlacement.innerHTML;
              blockPlacement.innerHTML = '';
              block.removeAttribute('data-moved-below-media');
            }
          } else {
            blockPlacement.innerHTML = block.innerHTML;
            block.innerHTML = '';
            block.setAttribute('data-moved-below-media', '');
          }
        });
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      get productForm() {
        return this.querySelector('form[is="product-form"');
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks,
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut,
          );

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant,
              },
            }),
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.innerHTML = FoxTheme.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    },
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal,
        });
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    },
  );
}
function syncCustomQuantityWithShopify() {
  const findShopifyElements = () => {
    const shopifyQuantityInput =
      document.querySelector("#Quantity-template--26681814516040__main") ||
      document.querySelector(".quantity__input") ||
      document.querySelector('input[name="quantity"]');

    const shopifyWrapper =
      shopifyQuantityInput?.closest("quantity-input") ||
      document.querySelector("quantity-input");

    const shopifyMinusBtn =
      shopifyWrapper?.querySelector('.quantity__button[name="minus"]') ||
      document.querySelector('.quantity__button[name="minus"]');

    const shopifyPlusBtn =
      shopifyWrapper?.querySelector('.quantity__button[name="plus"]') ||
      document.querySelector('.quantity__button[name="plus"]');

    return { shopifyQuantityInput, shopifyMinusBtn, shopifyPlusBtn };
  };
  
const waitForShopifyElements = () => {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        const { shopifyQuantityInput } = findShopifyElements();
        if (shopifyQuantityInput) {
          clearInterval(checkInterval);
          resolve(findShopifyElements());
        }
      }, 100);
      setTimeout(() => {
        clearInterval(checkInterval);
        resolve(null);
      }, 5000);
    });
  };
  const initializeQuantityControls = () => {
    waitForShopifyElements().then((elements) => {
      if (!elements || !elements.shopifyQuantityInput) {
        return;
      }
      
      document.querySelectorAll(".qty-wrapper").forEach((wrapper) => {
        
        if (wrapper.dataset.initialized === "true") {
          // Clean up old event listeners before reinitializing
          const oldInput = wrapper.querySelector(".qty-input");
          const oldMinus = wrapper.querySelector(".minus");
          const oldPlus = wrapper.querySelector(".plus");
          
          if (oldMinus) {
            const newMinus = oldMinus.cloneNode(true);
            oldMinus.parentNode.replaceChild(newMinus, oldMinus);
          }
          if (oldPlus) {
            const newPlus = oldPlus.cloneNode(true);
            oldPlus.parentNode.replaceChild(newPlus, oldPlus);
          }
          if (oldInput) {
            const newInput = oldInput.cloneNode(true);
            oldInput.parentNode.replaceChild(newInput, oldInput);
          }
          
          delete wrapper.dataset.initialized;
        }
        
        wrapper.dataset.initialized = "true";
        
        const input = wrapper.querySelector(".qty-input");
        const minus = wrapper.querySelector(".minus");
        const plus = wrapper.querySelector(".plus");
        
        if (!input || !minus || !plus) return;

        const { shopifyQuantityInput, shopifyMinusBtn, shopifyPlusBtn } =
          elements;

        const updateButtons = () => {
          const value = parseInt(input.value) || 1;
          if (minus) minus.disabled = value <= 1;
        };

        const syncToShopify = (quantity) => {
          if (shopifyQuantityInput) {
            shopifyQuantityInput.value = quantity;
            shopifyQuantityInput.dispatchEvent(
              new Event("change", { bubbles: true }),
            );
            shopifyQuantityInput.dispatchEvent(
              new Event("input", { bubbles: true }),
            );

            if (shopifyMinusBtn) {
              if (quantity <= 1) {
                shopifyMinusBtn.setAttribute("disabled", "disabled");
              } else {
                shopifyMinusBtn.removeAttribute("disabled");
              }
            }
          }
        };
        minus.addEventListener("click", (e) => {
          e.preventDefault();
          let value = parseInt(input.value) || 1;
          if (value > 1) {
            const newValue = value - 1;
            input.value = newValue;
            syncToShopify(newValue);
          }
          updateButtons();
        });

        plus.addEventListener("click", (e) => {
          e.preventDefault();
          let value = parseInt(input.value) || 1;
          const newValue = value + 1;
          input.value = newValue;
          syncToShopify(newValue);
          updateButtons();
        });

        input.addEventListener("change", (e) => {
          let value = parseInt(input.value) || 1;
          if (value < 1) value = 1;
          input.value = value;
          syncToShopify(value);
          updateButtons();
        });
        
        const initialValue = parseInt(input.value) || 1;
        syncToShopify(initialValue);
        updateButtons();
      });
    });
  };
  
  // Intial load with thiss DOM
  initializeQuantityControls();
  
  // Listen for variant changes
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList' && mutation.target.id === 'MainProduct-template--26681814516040__main') {
        console.log('Variant changed - reinitializing quantity controls');
        setTimeout(() => {
          initializeQuantityControls();
        }, 100);
        break;
      }
    }
  });
  
  // Start observing
  const productMain = document.querySelector('#MainProduct-template--26681814516040__main');
  if (productMain) {
    observer.observe(productMain, { childList: true, subtree: true });
  }
  
  const variantSelectors = document.querySelectorAll('input[name="id"], select[name="id"], .product-form__input input[type="radio"]');
  variantSelectors.forEach(selector => {
    selector.addEventListener('change', () => {
      setTimeout(() => {
        initializeQuantityControls();
      }, 100);
    });
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", syncCustomQuantityWithShopify);
} else {
  syncCustomQuantityWithShopify();
}
document.addEventListener('variant:change', () => {
  setTimeout(() => {
    syncCustomQuantityWithShopify();
  }, 100);
});


// ---- UPSELL productt added to Cartt ----
(function () {
  if (window._upsellInitialized) return;
  window._upsellInitialized = true;

  const UPSELL_VARIANT_ID = Number(
    document.getElementById("acc-pad")?.dataset.variantId,
  );
  if (!UPSELL_VARIANT_ID) return;

  let upsellPending = false;

  function syncUpsell(originalPublish, event, data) {
    setTimeout(() => {
      fetch("/cart.js")
        .then((r) => r.json())
        .then((cart) => {
          console.log("Fresh cart:", cart);

          const mainVariantId = Number(
            document.querySelector('form[is="product-form"] input[name="id"]')
              ?.value,
          );

          const mainQty =
            cart.items.find((item) => item.variant_id === mainVariantId)
              ?.quantity || 1;

          const existingUpsell = cart.items.find(
            (item) => item.variant_id === UPSELL_VARIANT_ID,
          );

          return existingUpsell
            ? fetch("/cart/change.js", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  line: existingUpsell.line,
                  quantity: mainQty,
                }),
              })
            : fetch("/cart/add.js", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  items: [
                    {
                      id: UPSELL_VARIANT_ID,
                      quantity: mainQty,
                    },
                  ],
                }),
              });
        })
        .then((res) => res.json())
        .then(() => {
          upsellPending = false;

          document.dispatchEvent(
            new CustomEvent("cart:refresh", {
              detail: {
                open: true,
              },
            }),
          );
        })
        .catch((err) => {
          console.error(err);
          upsellPending = false;

          document.dispatchEvent(
            new CustomEvent("cart:refresh", {
              detail: {
                open: true,
              },
            }),
          );
        });
    }, 300);
  }

  function initUpsellInterceptor() {
    if (!window.FoxTheme?.pubsub?.publish) return false;

    if (FoxTheme.pubsub._originalPublish) {
      FoxTheme.pubsub.publish = FoxTheme.pubsub._originalPublish;
    }

    FoxTheme.pubsub._originalPublish = FoxTheme.pubsub.publish.bind(
      FoxTheme.pubsub,
    );
    const originalPublish = FoxTheme.pubsub._originalPublish;

    FoxTheme.pubsub.publish = function (event, data) {
      if (
        event === FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate &&
        upsellPending
      ) {
        const checkbox = document.getElementById("acc-pad");
        if (!checkbox?.checked) {
          upsellPending = false;
          return originalPublish(event, data);
        }
        syncUpsell(originalPublish, event, data);
        return;
      }
      return originalPublish(event, data);
    };

    return true;
  }

  function attachClickListener() {
    document.addEventListener("click", function (e) {
      const btn = e.target.closest('form[is="product-form"] [name="add"]');
      if (!btn) return;
      const checkbox = document.getElementById("acc-pad");
      if (!checkbox?.checked) return;
      upsellPending = true;
    });
  }

  // ✅ No need to poll — FoxTheme is already initialized at this point
  // since we're inside product-info.js which runs after FoxTheme core
  if (initUpsellInterceptor()) {
    attachClickListener();
  } else {
    // Fallback poll just in case
    const interval = setInterval(() => {
      if (initUpsellInterceptor()) {
        clearInterval(interval);
        attachClickListener();
      }
    }, 50);
    setTimeout(() => clearInterval(interval), 10000);
  }
})();
